import React from "react";
import styled, { keyframes } from "styled-components";
import { ShoppingCart, UtensilsCrossed, Search, X, Clock } from "lucide-react";
import { getMenuItems, getCategories, getKitchenStatus } from "./mock";
import { useLanguage } from "../../../../contexts/language";
import { useOrder } from "../../../../contexts/order";
import { useRoom } from "../../../../contexts/room";
import { RoomInputModal } from "../../../../components/room-input-modal";
import { PageHeader } from "../../../../components/page-header";
import { MenuItem } from "../../../../components/menu-item";
import { CartDrawer } from "../../../../components/cart-drawer";
import { KitchenScheduleModal } from "../../../../components/kitchen-schedule-modal";

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fff 0%, #f7f7f7 45%, #f5f5f5 100%);
  padding-bottom: 80px;
`;


const CategorySelector = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 12px;
  overflow-x: auto;
  display: flex;
  gap: 8px;
  padding-bottom: 8px;
  padding-top: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryButton = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => (props.$active ? "#fff" : "#333")};
  border-radius: 20px;
  white-space: nowrap;
  background-color: ${(props) => (props.$active ? "#c9a961" : "#f0f0f0")};
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: #c9a961;
    color: #fff;
  }
`;

const ContentContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 18px 12px 24px 12px;
`;

const IntroCard = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const IntroRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IntroTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #2f2f2f;
`;

const IntroText = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 4px;
`;

const KitchenStatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 719px) {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const KitchenStatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};

  @media (max-width: 719px) {
    font-size: 11px;
  }
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};
`;

const ViewScheduleButton = styled.button`
  border: none;
  background: none;
  color: #c9a961;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  flex-shrink: 0;

  @media (max-width: 719px) {
    font-size: 11px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const ClosedBanner = styled.div`
  background: linear-gradient(135deg, #fff8e1, #fff3cd);
  border: 1px solid #f0d78c;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
`;

const ClosedBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClosedBannerText = styled.div`
  font-size: 12px;
  color: #856404;
  margin-top: 4px;
  opacity: 0.85;
`;

const ClosedBannerActions = styled.div`
  margin-top: 10px;
`;

const ScheduleLinkButton = styled.button`
  border: 1px solid #c9a961;
  background: #fff;
  color: #c9a961;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: #c9a961;
    color: #fff;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: calc(100% - 56px);
  padding: 10px 36px 10px 38px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  font-size: 14px;
  color: #333;
  background-color: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 2px rgba(201, 169, 97, 0.15);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: #e5e5e5;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  padding: 0;
  transition: background-color 0.15s;

  &:hover {
    background: #d5d5d5;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #999;
  font-size: 14px;
`;

const skeletonPulse = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const Skeleton = styled.div`
  height: ${(props) => props.$height || "16px"};
  width: ${(props) => props.$width || "100%"};
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${skeletonPulse} 1.5s ease-in-out infinite;
`;

const SkeletonCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
`;

const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const CategoryTitle = styled.h2`
  position: sticky;
  top: 95px;
  background-color: #f9f9f9;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  border-bottom: 2px solid #c9a961;
  padding-bottom: 6px;
  padding-top: 6px;
  z-index: 1;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OutOfStockTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #999;
  margin-top: 40px;
  margin-bottom: 12px;
  border-bottom: 2px solid #e5e5e5;
  padding-bottom: 6px;
`;

const OutOfStockSection = styled.div`
  margin-bottom: 20px;
  opacity: 0.5;
`;

const OutOfStockCategory = styled.h3`
  font-size: 13px;
  font-weight: 500;
  color: #999;
  margin-bottom: 8px;
`;

const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #c9a961;
  color: #fff;
  border: none;
  border-radius: 30px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 169, 97, 0.4);
  font-size: 14px;
  font-weight: 600;
  z-index: 3;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5);
  }
`;

const CartIconWrapper = styled.div`
  position: relative;
`;

const CartBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #fff;
  color: #c9a961;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
`;

const UnnamedComponent = (props) => {
  const { building_code } = props;
  const { t, language } = useLanguage();
  const { getCartItemCount, getCartTotal } = useOrder();
  const { roomNumber, guestName, loadCachedProfile } = useRoom();
  const [items, setItems] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCart, setShowCart] = React.useState(false);
  const [showRoomModal, setShowRoomModal] = React.useState(false);
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [kitchenStatus, setKitchenStatus] = React.useState(null);
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);
  const cartItemCount = getCartItemCount();
  const totalPrice = getCartTotal();

  const isKitchenClosed = kitchenStatus && kitchenStatus.is_open === false;

  React.useEffect(() => {
    const cachedLoaded = loadCachedProfile(building_code);
    if (!cachedLoaded && !guestName) {
      setShowRoomModal(true);
    }
  }, [building_code, guestName, loadCachedProfile]);

  React.useEffect(() => {
    const loadMenuData = async () => {
      try {
        const [menuItems, menuCategories] = await Promise.all([
          getMenuItems(),
          getCategories(),
        ]);
        setItems(menuItems);
        setCategories(menuCategories);
        const status = await getKitchenStatus();
        setKitchenStatus(status);
      } finally {
        setIsLoading(false);
      }
    };

    loadMenuData();
  }, []);

  const query = searchQuery.trim().toLowerCase();
  const matchesSearch = (item) => {
    if (!query) return true;
    return (
      (item.name || "").toLowerCase().includes(query) ||
      (item.description || "").toLowerCase().includes(query)
    );
  };

  const availableItems = items.filter((item) => item.stock > 0 && matchesSearch(item));
  const outOfStockItems = items.filter((item) => item.stock === 0 && matchesSearch(item));

  const groupByCategory = (itemList) => {
    return categories.reduce((acc, category) => {
      const categoryItems = itemList.filter(
        (item) => item.category === category,
      );
      if (categoryItems.length > 0) {
        acc[category] = categoryItems;
      }
      return acc;
    }, {});
  };

  const availableByCategory = groupByCategory(availableItems);
  const outOfStockByCategory = groupByCategory(outOfStockItems);

  const scrollToCategory = (category) => {
    setActiveCategory(category);
    const element = document.getElementById(
      `category-${category.toLowerCase()}`,
    );
    if (element) {
      const headerHeight = 100;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  const getKitchenSubtitle = () => {
    if (!kitchenStatus) return "";
    if (kitchenStatus.is_open && kitchenStatus.current_slot_close) {
      return `${t.kitchen.closesAt} ${kitchenStatus.current_slot_close}`;
    }
    if (!kitchenStatus.is_open && kitchenStatus.next_opening) {
      const next = kitchenStatus.next_opening;
      const dayName = language === "id" ? next.day_id : next.day_en;
      const isSameDay = next.day_of_week === kitchenStatus.server_day;
      if (isSameDay) return `${t.kitchen.opensAt} ${next.open_time}`;
      return `${t.kitchen.opensAt} ${dayName} ${next.open_time}`;
    }
    return t.kitchen.closedToday;
  };

  return (
    <PageContainer>
      <PageHeader
        title={t.order.title}
        showBackButton={true}
        useHomeIcon={true}
        backPath={`/hebe/${building_code}/hgs/`}
        showProfileAction={true}
        onChangeProfile={() => setShowRoomModal(true)}
        autoPromptNotification={true}
        buildingCode={building_code}
      >
        <CategorySelector>
          {categories.map((category) => (
            <CategoryButton
              key={category}
              $active={activeCategory === category}
              onClick={() => scrollToCategory(category)}
            >
              {category}
            </CategoryButton>
          ))}
        </CategorySelector>
      </PageHeader>

      <ContentContainer>
        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i}>
                <Skeleton $width="88px" $height="88px" style={{ flexShrink: 0, borderRadius: "10px" }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", paddingTop: "4px" }}>
                  <Skeleton $width="60%" $height="14px" />
                  <Skeleton $width="85%" $height="12px" />
                  <Skeleton $width="30%" $height="12px" />
                </div>
              </SkeletonCard>
            ))}
          </div>
        ) : (
          <>
            <IntroCard>
              <IntroRow>
                <UtensilsCrossed size={20} color="#c9a961" />
                <IntroTitle>{t.order.freshlyPrepared}</IntroTitle>
              </IntroRow>
              <IntroText>
                {t.order.pickYourMenu}
              </IntroText>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", padding: "6px 10px", background: "#fdf6ec", borderRadius: "8px", fontSize: "11px", color: "#92660a" }}>
                <span style={{ flexShrink: 0 }}>📍</span>
                <span>{t.order.pickupInfo}</span>
              </div>
              {kitchenStatus && (
                <KitchenStatusRow>
                  <KitchenStatusBadge $isOpen={kitchenStatus.is_open}>
                    <StatusDot $isOpen={kitchenStatus.is_open} />
                    {kitchenStatus.is_open ? t.kitchen.open : t.kitchen.closed}
                    <span style={{ fontWeight: 400, color: "#777", marginLeft: "4px" }}>
                      {getKitchenSubtitle()}
                    </span>
                  </KitchenStatusBadge>
                  <ViewScheduleButton onClick={() => setShowScheduleModal(true)}>
                    <Clock size={12} />
                    {t.kitchen.viewSchedule}
                  </ViewScheduleButton>
                </KitchenStatusRow>
              )}
            </IntroCard>

            {isKitchenClosed && (
              <ClosedBanner>
                <ClosedBannerTitle>
                  <Clock size={16} />
                  {t.kitchen.closed}
                </ClosedBannerTitle>
                <ClosedBannerText>
                  {t.kitchen.closedMessage}
                </ClosedBannerText>
                <ClosedBannerActions>
                  <ScheduleLinkButton onClick={() => setShowScheduleModal(true)}>
                    <Clock size={12} />
                    {t.kitchen.viewSchedule}
                  </ScheduleLinkButton>
                </ClosedBannerActions>
              </ClosedBanner>
            )}

            <SearchWrapper>
              <SearchIcon><Search size={16} /></SearchIcon>
              <SearchInput
                type="text"
                placeholder={t.order.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearButton onClick={() => setSearchQuery("")}>
                  <X size={12} />
                </ClearButton>
              )}
            </SearchWrapper>

            {query && availableItems.length === 0 && outOfStockItems.length === 0 ? (
              <NoResults>{t.order.noResults}</NoResults>
            ) : null}

            {Object.keys(availableByCategory).map((category) => (
              <CategorySection
                key={category}
                id={`category-${category.toLowerCase()}`}
              >
                <CategoryTitle>{category}</CategoryTitle>
                <ItemList>
                  {availableByCategory[category].map((item) => (
                    <MenuItem
                      key={item.id}
                      item={item}
                      isKitchenClosed={!!isKitchenClosed}
                    />
                  ))}
                </ItemList>
              </CategorySection>
            ))}

            {Object.keys(outOfStockByCategory).length > 0 && (
              <>
                <OutOfStockTitle>{t.order.outOfStock}</OutOfStockTitle>
                {Object.keys(outOfStockByCategory).map((category) => (
                  <OutOfStockSection key={`${category}-oos`}>
                    <OutOfStockCategory>{category}</OutOfStockCategory>
                    <ItemList>
                      {outOfStockByCategory[category].map((item) => (
                        <MenuItem
                          key={item.id}
                          item={item}
                          isOutOfStock={true}
                        />
                      ))}
                    </ItemList>
                  </OutOfStockSection>
                ))}
              </>
            )}
          </>
        )}
      </ContentContainer>

      {cartItemCount > 0 && !isKitchenClosed && (
        <FloatingCartButton onClick={() => setShowCart(true)}>
          <CartIconWrapper>
            <ShoppingCart size={20} />
            <CartBadge>{cartItemCount}</CartBadge>
          </CartIconWrapper>
          <span>
            {t.order.cart.checkout} • Rp{Math.round(totalPrice).toLocaleString("id-ID")}
          </span>
        </FloatingCartButton>
      )}

      {showCart && (
        <CartDrawer
          onClose={() => setShowCart(false)}
          building_code={building_code}
        />
      )}
      {showRoomModal && (
        <RoomInputModal
          onRoomVerified={() => setShowRoomModal(false)}
          buildingCode={building_code}
        />
      )}
      {showScheduleModal && (
        <KitchenScheduleModal
          kitchenStatus={kitchenStatus}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </PageContainer>
  );
};

export default UnnamedComponent;
